import React from 'react';
import { SignInPage, ProxiedSignInPage } from '@backstage/core-components';
import { 
  microsoftAuthApiRef, 
  IdentityApi, 
  SignInPageProps,
  configApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/**
 * Custom SignInPage that supports two authentication modes based on USE_LOCAL_DEV_SETUP:
 * 
 * 1. USE_LOCAL_DEV_SETUP=true (default for local development):
 *    - Shows a guest login option
 *    - User is signed in with a guest identity (still has user context)
 *    - No Azure AD configuration required
 *    - Also uses in-memory SQLite database (configured in backend)
 * 
 * 2. USE_LOCAL_DEV_SETUP=false (production-like or when testing Entra ID auth):
 *    - Uses Microsoft/Entra ID authentication only
 *    - On first visit or returning with valid session: auto-login is triggered
 *    - After explicit logout: shows sign-in button so user can choose different account
 *    - Uses PostgreSQL database (configured in backend)
 * 
 * The mode is determined by 'app.useLocalDevSetup' config value, which is set
 * in the config files (true in app-config.yaml, false in app-config.overrides-entra.yaml).
 * 
 * The logout flag is stored in localStorage (not sessionStorage) because the signOut
 * redirects to a new page, which would lose sessionStorage state.
 */

const LOGOUT_FLAG_KEY = 'backstage-explicit-logout';

const wasExplicitLogout = (): boolean => {
  return localStorage.getItem(LOGOUT_FLAG_KEY) === 'true';
};

const setExplicitLogoutFlag = () => {
  localStorage.setItem(LOGOUT_FLAG_KEY, 'true');
};

const clearExplicitLogoutFlag = () => {
  localStorage.removeItem(LOGOUT_FLAG_KEY);
};

const microsoftProvider = {
  id: 'microsoft-auth-provider',
  title: 'Siemens Healthineers',
  message: 'Sign in with teamplay Entra ID',
  apiRef: microsoftAuthApiRef,
};

/**
 * Wraps the IdentityApi to intercept signOut and set the logout flag.
 * This allows us to detect explicit logouts and skip auto-login.
 */
const wrapIdentityApiWithLogoutFlag = (identityApi: IdentityApi): IdentityApi => {
  return {
    getProfileInfo: () => identityApi.getProfileInfo(),
    getBackstageIdentity: () => identityApi.getBackstageIdentity(),
    getCredentials: () => identityApi.getCredentials(),
    signOut: async () => {
      // Set the flag BEFORE signing out (before redirect happens)
      setExplicitLogoutFlag();
      return identityApi.signOut();
    },
  };
};

/**
 * Determines if we should use local dev setup (guest login) based on config.
 * Checks app.useLocalDevSetup config value set in app-config.yaml or overlay configs.
 */
const useLocalDevMode = (): boolean => {
  const configApi = useApi(configApiRef);
  
  // Check if useLocalDevSetup is explicitly set in config
  const useLocalDevSetupConfig = configApi.getOptionalString('app.useLocalDevSetup');
  
  // Only use guest login if explicitly set to 'true'
  // This ensures production (where it's 'false' or not set) uses Entra ID
  if (useLocalDevSetupConfig === 'true') {
    return true;
  }
  
  // Default to Entra ID authentication (production-safe default)
  return false;
};

export const CustomSignInPage = (props: SignInPageProps) => {
  const useLocalDev = useLocalDevMode();
  
  // Check if this was an explicit logout - only check once on mount
  const [skipAuto] = React.useState(() => wasExplicitLogout());
  
  const handleSignInSuccess = React.useCallback((identityApi: IdentityApi) => {
    // Clear the logout flag on successful sign-in
    clearExplicitLogoutFlag();
    
    // Wrap the identity API to intercept future signOut calls
    const wrappedIdentityApi = wrapIdentityApiWithLogoutFlag(identityApi);
    
    // Pass the wrapped identity API to the original callback
    props.onSignInSuccess(wrappedIdentityApi);
  }, [props]);

  // When USE_LOCAL_DEV_SETUP=true, use the guest authentication
  if (useLocalDev) {
    return (
      <ProxiedSignInPage
        {...props}
        provider="guest"
      />
    );
  }

  // When USE_LOCAL_DEV_SETUP=false, use Microsoft/Entra ID authentication only
  if (skipAuto) {
    // After explicit logout: show sign-in page with button (no auto)
    return (
      <SignInPage
        {...props}
        onSignInSuccess={handleSignInSuccess}
        provider={microsoftProvider}
      />
    );
  }

  // Normal flow: auto sign-in with Microsoft
  return (
    <SignInPage
      {...props}
      onSignInSuccess={handleSignInSuccess}
      auto
      provider={microsoftProvider}
    />
  );
};
